import classNames from 'classnames';
import { routerActions } from 'connected-react-router';
import React, { useEffect, useRef, useState } from 'react';
import { isBrowser } from "react-device-detect";
import { useDispatch } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import 'webrtc-client-janus/dist/index.css';
import { redirectToLogin, redirectToLogout } from '.';
import * as api from './api';
import { getExtensionsByIds } from './api';
import { getContactsByIds } from './api/contact-control';
import { getQueuesByIds } from './api/queue-control';
import { AsyncStringResponse } from './api/transcriptions';
import './App.scss';
import AgentChartsPopup from './Components/AgentChartsPopup';
import ExtensionMenu from './Components/AgentMenu';
import AgentView from './Components/AgentView';
import { QueueCharts } from './Components/AgentView/Dashboard';
import CallPopup from './Components/CallPopup';
import CallsPage from './Components/CallsPage';
import ContactsPage, { ContactMenu } from './Components/ContactsPage';
import DirectoryPage from './Components/DirectoryPage';
import ExtensionsPage from './Components/ExtensionsPage';
import InstantMessagerPage from './Components/InstantMessagerPage';
import MainToolbar from './Components/MainToolbar';
import { MenuTarget, targetFromEvent } from './Components/Menu';
import MobileCallsPage from './Components/MobileView/CallsPage';
import MobileToolbar from './Components/MobileView/MobileToolbar';
import MobilePresencePage from './Components/MobileView/PresencePage';
import MobileVoicemailPage from './Components/MobileView/VoicemailPage';
import Modal from './Components/Modal';
import { PopperHost } from './Components/PortalPopper';
import { QueueMenu } from './Components/QueueMenu';
import QueuesPage from './Components/QueuesPage';
import SearchPage from './Components/SearchPage';
import TeamsTabConfig from './Components/TeamsTabConfig';
import Toaster from './Components/Toaster';
import VoiceMailPage from './Components/VoiceMailPage';
import VoiceMailPanel from './Components/VoiceMailPanel';
import { useSelector } from './hook-utils';
import { AlertIconType, closeSmartSearchDialogTeams, showAlertDialog } from './legacy';
import { MobilityClientApi } from './mobility-client-api';
import { CallState, ContactBasic, ContactType, EntityType } from './model';
import { actions } from './store';
import { selectConfigProp, selectShowShortcutsToolbar, selectMyAccess, selectMyself, selectShowMainToolbar, selectApplicationSetting } from './store/reducers/config';
import { selectClientLogging, selectTheme } from './store/reducers/localSettings';
import { selectQueueStatsModal, selectShowNoLicensesAvailable } from './store/reducers/modals';
import { selectExtension } from './store/reducers/queueAgents';
import { selectQueue } from './store/reducers/queues';
import { useTeams } from './TeamsProvider';
import CrmIntegrationView from './Components/CrmIntegrationView';
import ShortcutsBar from './Components/ShortcutsBar';
import { useElectronTelephoneController } from './Electron/telephone-controller';
import { IpcRenderer, IpcRendererEvent, IpcRenderOn, IpcRenderRemove, IpcRenderSend } from "./Electron/ipcRenderer";
import { selectCrmSettings } from './store/reducers/crmIntegration';
import Button from './Components/Button';
import AchieveApi from './Components/AchieveApi';
import  AchieveExtension from './Components/AchieveExtension';
import { EndpointView } from 'webrtc-client-janus/dist/mediator/janussession';
import { selectMyMedia } from './store/reducers/media';

declare global {
  interface Window {
    ipcRenderer: IpcRenderer
    CitrixWebRTC: any
    citrixVdaSupport: boolean
    ReactNativeWebView: ReactNativeWebView
    MobilityClientApi: MobilityClientApi
    endpointView: EndpointView
  }
  interface MediaDevices {
    getDisplayMedia(constraints?: MediaStreamConstraints): Promise<MediaStream>;
  }


  // if constraints config still lose some prop, you can define it by yourself also
  interface MediaTrackConstraintSet {
    displaySurface?: ConstrainDOMString;
    logicalSurface?: ConstrainBoolean;
    // m
  }
}

interface ReactNativeWebView {
  postMessage(message: string): string;
}

export interface ContextMenuEvent extends Event {
  detail: {
    entityId: string;
    entityType: number;
    entityServer: string;
  }
}

export interface CreateContactEvent extends Event {
  detail: string
}

const _log = console.log
const _error = console.error
const _warning = console.warn
const _info = console.info
const _debug = console.debug

interface MenuState {
  target: MenuTarget
  entityId: string
  enntityType: EntityType
  entityServer: string
}

const App: React.FC = () => {
  const dispatch = useDispatch()
  const [ready, setReady] = useState(false)
  const theme = useSelector(selectTheme)
  const queueStatsModal = useSelector(selectQueueStatsModal)
  const logging = useSelector(selectClientLogging)
  const debugging = useSelector(s => selectConfigProp(s, 'clientDebugging'))

  const myId = useSelector(selectMyself)
  const [menuState, setMenuState] = useState<MenuState>()
  const extension = useSelector(s => menuState && menuState.enntityType === EntityType.Extension && selectExtension(s, { id: menuState.entityId, server: menuState.entityServer }))
  const queue = useSelector(s => menuState && menuState.enntityType === EntityType.Queue && selectQueue(s, { id: menuState.entityId, server: menuState.entityServer }))
  const [contact, setContact] = useState<ContactBasic>()  
  const access = useSelector(selectMyAccess)
  const showShortcutsToolbar = useSelector(selectShowShortcutsToolbar)
  const showMainToolbar = useSelector(selectShowMainToolbar)
  useElectronTelephoneController()
  const { integrationEnabled: crmEnabled } = useSelector(selectCrmSettings)
  const showNoLicenseAvailable = useSelector(selectShowNoLicensesAvailable)

  const media = useSelector(selectMyMedia)
  const prevMedia = useRef(CallState.None)
  const view = useSelector(s => selectApplicationSetting(s, 'AgentViewOnAnswer'))

  // Open the Agent View if there is a new incoming call
  useEffect(() => {
    const call = media.find(m => m.status === CallState.ExtensionAnswered)
    if (call){
      if (prevMedia.current === CallState.None) {
        if (media !== null && (!view || view && view.value === 'true')) {
          dispatch(actions.openAgentView(call.id))
          prevMedia.current = CallState.ExtensionAnswered
        }
      }
    } else {
      prevMedia.current = CallState.None
    }})

  useEffect(() => {
    if (ready) {
      dispatch(actions.init())
    }
  }, [dispatch, ready])

  useEffect(() => {
    const result = (event: IpcRendererEvent, ...args: any[]) => {
      console.log(event, args)
    }

    const handler = (event: IpcRendererEvent, arg: api.FatClientOperation) => {
      console.log(event, arg)
      api.showFatClientUi(arg)
    }

    const logIdleReport = (event: IpcRendererEvent, report: { UserIdleTime: number }) => dispatch(actions.setIdleSeconds(report.UserIdleTime))

    IpcRenderOn('teams-cti-command-result', result)
    IpcRenderOn('teams-client-controller-result', result)
    IpcRenderOn('app-launcher-result', result)
    IpcRenderOn('broadcast-client-show-ui', handler)
    IpcRenderOn('user-idle-time-report', logIdleReport)

    return () => {
      IpcRenderRemove('teams-cti-command-result', result)
      IpcRenderRemove('teams-client-controller-result', result)
      IpcRenderRemove('app-launcher-result', result)
      IpcRenderRemove('broadcast-client-show-ui', handler)
      IpcRenderRemove('user-idle-time-report', logIdleReport)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const element = e.target as HTMLElement
      if (element) {
        switch (element.nodeName) {
          case 'INPUT':
          case 'SELECT':
          case 'TEXTAREA':
          case 'OPTION':
          case 'FIELDSET':
            break;
          case 'DIV':
            let found = false
            element.classList.forEach(classname => {
              if (classname.indexOf('mce-edit-focus') === 0 && !found) {
                found = true
              }
            });
            if (!found) {
              dispatch(actions.windowKeyDown(e.keyCode))
            }
            break;
          default: {
            dispatch(actions.windowKeyDown(e.keyCode))
          }
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      dispatch(actions.setFocus(true))
    };
    document.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('focus', handleFocus)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      dispatch(actions.setFocus(false))
    }
    document.addEventListener('blur', handleFocus)

    return () => {
      document.removeEventListener('blur', handleFocus)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handle = (e: any) => {
      const event = e as ContextMenuEvent
      if (event.detail) {
        const target = targetFromEvent(e)
        setMenuState({
          target: target,
          entityId: event.detail.entityId,
          enntityType: event.detail.entityType,
          entityServer: event.detail.entityServer,
        })
      }
    }

    document.addEventListener('custom', handle)

    return () => {
      document.removeEventListener('custom', handle)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handle = (e: any) => {
      const event = e as CreateContactEvent
      if (event.detail) {
        dispatch(actions.createCustomerContact(event.detail))
      }
    }

    document.addEventListener('create-contact', handle)

    return () => {
      document.removeEventListener('create-contact', handle)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let mounted = true
    load()
    return () => { mounted = false }

    async function load() {
      if (!menuState || !myId) return
      if (menuState.enntityType === EntityType.Extension && extension) return

      //get extension data for context menu
      if (menuState.enntityType === EntityType.Extension && !extension) {

        const extensions = await getExtensionsByIds(myId, [{ id: menuState.entityId, server: menuState.entityServer }], 0)

        if (!mounted) { return }

        dispatch(actions.fetchExtensionsByIdsResponse(extensions.rows))

        return
      }

      //get queue data for context menu
      if (menuState.enntityType === EntityType.Queue && !queue) {

        const queues = await getQueuesByIds(myId, [{ id: menuState.entityId, server: menuState.entityServer }])

        if (!mounted) { return }

        dispatch(actions.fetchQueuesByIdsResponse(queues))
      }

      //get contact data for context menu
      if (menuState.enntityType === EntityType.Contact && !contact) {

        const contact = await getContactsByIds(myId, [{ id: menuState.entityId, server: menuState.entityServer }])

        if (!mounted || contact.rows.length === 0) { return }

        setContact(contact.rows[0])
      }
    }

  }, [contact, dispatch, extension, menuState, myId, queue])

  useEffect(() => {
    const handle = async (e: BeforeUnloadEvent) => {
      await dispatch(actions.shutdown())
    };
    window.addEventListener('beforeunload', handle)

    return () => {
      window.removeEventListener('beforeunload', handle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handle = async (event: Event | string, source?: string, fileno?: number, columnNumber?: number, error?: Error) => {
      /*Add your global exception handling logic here*/
      console.error(event, source, fileno, columnNumber, error)
    };
    window.addEventListener('onerror', handle)

    return () => {
      window.removeEventListener('onerror', handle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
  useEffect(() => {
    const onVisibilityChange = () => {
      //dispatch(actions.setFocus(false))
    }

    window.addEventListener('visibilitychange', onVisibilityChange, false);

    return () => {
      window.removeEventListener('visibilitychange', onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
*/
  /*

  //SCREEN RECORDING
    useEffect(() => {
      if (process.env.NODE_ENV !== 'development') return
      if (!media) return

      media.forEach(call => {
        if (call.status === CallState.ExtensionAnswered) {
          dispatch(actions.startScreenRecording(call.id))
        }
      })

      if (!prevMedia) return

      prevMedia.forEach(call => {
        if (media.length === 0 || !media.find(x => x.id === call.id))
          dispatch(actions.stopScreenRecording(call.id))
      },
      )

    }, [dispatch, media, prevMedia])
  */

  useEffect(() => {
    function log(level: string, orig: (message?: any, ...optionalParams: any[]) => void) {
      return function (message?: any, ...optionalParams: any[]) {
        if (logging) {
          if (arguments.length > 0) {
            const a = arguments[0].toString()
            if (a !== '%c prev state' && a !== '%c next state') {
              IpcRenderSend('client-logging', { LogLevel: level, Log: JSON.stringify(arguments) })
            }
          }
        }
        if (debugging || process.env.NODE_ENV === 'development') {
          orig.apply(console, [message, ...optionalParams])
        }
      }
    }

    console.error = log('Error', _error)
    console.log = log('Info', _log)
    console.warn = log('Warn', _warning)
    console.info = log('Info', _info)
    console.debug = log('Debug', _debug)

    return () => {
      console.error = _error 
      console.log = _log 
      console.warn = _warning
      console.info = _info
      console.debug = _debug 
    }
  }, [logging, debugging])

  const teams = useTeams()

  useEffect(() => {
    teams.setTheme(theme)
  }, [teams, theme])

  useEffect(() => {
    tryGetToken()

    async function tryGetToken() {
      if (!teams.init) { return }

      if (api.hasClientId()) {
        setReady(true)
        return
      }

      if (!teams.inTeams || window.location.pathname.indexOf('/sso/') === -1) {
        redirectToLogin(window.location.search)
        return
      }

      try {

        const token = await teams.getToken()
        const result = await api.parseJson<AsyncStringResponse>(await api.handler('/Teams/Handlers/Login.ashx', {
          command: 'auto',
          token: token,
          'client': '',
        }, false))

        if (result.success) {
          if (result.data === "redirect") {
            //server is not configured so redirect
            redirectToLogin(window.location.search)

          } else {

            api.setClientId(result.data)
            setReady(true)

          }

        } else {
          //have an issue or is not configured so not showing error
          redirectToLogin(window.location.search)
        }
      } catch (err) {
        console.error(err)

        showAlertDialog('Error', 'Single sign on has failed', AlertIconType.Error, () => {
          redirectToLogin(window.location.search)
        })

      }
    }
  }, [teams])

  const preventContextMenu = process.env.NODE_ENV === 'development' ? undefined : (e: React.MouseEvent) => {
    if (e.target instanceof HTMLElement) {
      if (e.target.isContentEditable || e.target.nodeName === 'INPUT' || e.target.nodeName === 'TEXTAREA') {
        return
      }
    }

    e.preventDefault()
  }

  if (!ready) { return null }

  if (isBrowser) {
    return <div className={classNames('App', `theme-${teams.theme}`)} onContextMenuCapture={preventContextMenu} onSelect={() => false}>
      <Switch>
        <Route path='/config-teams' component={TeamsTabConfig} />
        <Route render={browserRender} />
      </Switch>
      <PopperHost className={'menuHost'} />
    </div>
  } else {
    return <div className={classNames('App', `theme-${teams.theme}`)} onContextMenuCapture={preventContextMenu} >
      <Switch>
        <Route render={mobileRender} />
      </Switch>
      <PopperHost className={'menuHost'} />
    </div>
  }

  function browserRender() {
    const color = theme === 'light' ? '#FFF' : theme === 'dark' ? '#2d2c2c' : '#000'
    const style = document.createElement('style');
    style.appendChild(document.createTextNode('.ui-dialog { min-width: 660px !important; }'));
    style.appendChild(document.createTextNode('.dept-options .fieldset-title, .extension-advanced-settings .fieldset-title { background-color: ' + color + '; }'));
    document.head.appendChild(style);

    console.info('Render BrowserRender')

    return <>
      {showMainToolbar && <MainToolbar />}
      {access.features.extensionShortCuts && showShortcutsToolbar && <ShortcutsBar />}
      <main>
        <Switch>
          <Redirect exact from='/' to='/queues' />
          <Route path='/queues' component={QueuesPage} />
          <Route path='/agent/:id?/:tab?' component={AgentView} />
          <Route path='/vm' component={VoiceMailPage} />
          <Route path='/calls' component={CallsPage} />
          <Route path='/search' component={SearchPage} />
          <Route path='/chat' component={InstantMessagerPage} />
          <Route path='/directory' component={DirectoryPage} />
          <Route path='/extensions' component={ExtensionsPage} />
          <Route path='/contacts' component={ContactsPage} />          
          <Route path="/achieve" component={AchieveApi} />
          <Route path="/achievevm" component={AchieveApi} />      
          <Route path="/achieveextension/:extension?" component={AchieveExtension} />     
        </Switch>
      </main>
      {crmEnabled && <CrmIntegrationView />}
      <CallPopup />
      <Toaster />
      {menuState && extension && <ExtensionMenu {...menuState} extension={extension} onClose={() => {
        setMenuState(undefined)
      }} onClick={() => { closeSmartSearchDialogTeams() }} />}
      {menuState && queue && <QueueMenu {...menuState} queue={queue} onClose={() => {
        setMenuState(undefined)
      }} onClick={() => { closeSmartSearchDialogTeams() }} />}
      {menuState && contact && <ContactMenu state={{ target: menuState.target, item: contact }}
        onClose={() => {
          setMenuState(undefined)
        }}
        onOpen={(item) => {
          dispatch(routerActions.replace('/directory'))
          dispatch(actions.setDirectoryView({ regionId: item.regionId, entityType: EntityType.Contact, contactType: item.extensionNumber === '-1' ? ContactType.Customer : item.extensionNumber === '0' ? ContactType.Company : ContactType.Personal }))
          dispatch(actions.setSelectedContact(item))
        }}
        onNew={(item) => {
          dispatch(routerActions.replace('/directory'))
          dispatch(actions.setDirectoryView({ regionId: item.regionId, entityType: EntityType.Contact, contactType: item.extensionNumber === '-1' ? ContactType.Customer : item.extensionNumber === '0' ? ContactType.Company : ContactType.Personal }))
          dispatch(actions.setSelectedContact({ id: '', server: '' }))
        }}
        onClick={() => { closeSmartSearchDialogTeams() }} />}
      {queueStatsModal && <Modal isOpen onRequestClose={() => dispatch(actions.openQueueStats(null))}>
        <Modal.Header>Queue {queueStatsModal.id}</Modal.Header>
        <QueueCharts queue={queueStatsModal} />
      </Modal>}

      {showNoLicenseAvailable && <Modal isOpen>
        <Modal.Header>No licences available</Modal.Header>
        <div className={'NoLicenseModal'}>
          <span>There are no licenses available. Click continue to try again.</span>
          <Button primary onClick={() => redirectToLogout()}>Continue</Button>
        </div>

      </Modal>}

      <AgentChartsPopup />
      <VoiceMailPanel />
    </>
  }

  function mobileRender() {

    const style = document.createElement('style');
    style.appendChild(document.createTextNode('.ui-dialog { min-width: calc(100%); }'));
    document.head.appendChild(style);

    return <>
      <MobileToolbar />
      <main>
        <Switch>
          <Redirect exact from='/' to='/directory' />
          <Route path='/directory' component={DirectoryPage} />
          <Route path='/presence/:id/:server' component={MobilePresencePage} />
          <Route path='/vm' component={MobileVoicemailPage} />
          <Route path='/calls' component={MobileCallsPage} />
          <Route path='/queues' component={QueuesPage} />
        </Switch>
      </main>
      <Toaster />
    </>
  }
}

export default App

export function getIsDocumentHidden() {
  return !document['hidden']
}
